import { useSelectionFunctions } from '@/features/symbol-selector';

import { getSymbolsFromRow, getSymbolsFromRows } from '../lib/getSymbolsFromRows';

import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../../types';

const useCommoditiesSelection = (sections: CommoditiesTableSectionType[]) => {
  const {
    addSymbolsToSelection,
    areAllSymbolsSelected,
    areAnySymbolsSelected,
    isSpotLocked,
    isSymbolSelected,
    removeSymbolFromSelection,
    removeSymbolsFromSelection
  } = useSelectionFunctions();

  const getSelectableSymbols = (rows: CommoditiesTableRowType[]) =>
    getSymbolsFromRows(rows.filter(row => row.last || row.diff));

  const isSectionSelected = (rows: CommoditiesTableRowType[]): boolean => {
    const symbols = getSelectableSymbols(rows);
    if (symbols.length < 1) return false;
    return areAllSymbolsSelected(symbols);
  };

  const toggleSectionInSelection = (rows: CommoditiesTableRowType[]) => {
    if (isSectionSelected(rows)) removeSectionFromSelection(rows);
    else addSectionToSelection(rows);
  };

  const removeSectionFromSelection = (rows: CommoditiesTableRowType[]) => {
    for (const row of rows) {
      const last = row.symbol.key;
      const diff = row.diffSymbol.key;
      removeSymbolFromSelection(last);
      removeSymbolFromSelection(diff);
    }
  };

  const addSectionToSelection = (rows: CommoditiesTableRowType[]) => {
    const symbols = getSelectableSymbols(rows);
    addSymbolsToSelection(symbols);
  };

  const toggleCodeInSelection = (row: CommoditiesTableRowType, codeSelected: boolean): void => {
    const symbols = getSymbolsFromRow(row);
    if (codeSelected) {
      for (const symbol of symbols) {
        removeSymbolFromSelection(symbol);
      }
    } else {
      addSymbolsToSelection(symbols);
    }
  };

  const getAllSymbolsInTab = () =>
    sections.flatMap(({ rows }) => {
      return getSelectableSymbols(rows);
    });

  const addAllCodesToSelection = () => {
    const newSymbols = getAllSymbolsInTab();
    addSymbolsToSelection(newSymbols);
  };

  const clearAllCodesFromSelection = () => {
    removeSymbolsFromSelection(getAllSymbolsInTab());
  };

  const allCodesSelected = () => {
    if (sections.length < 1) return false;
    return sections.every(section => {
      return isSectionSelected(section.rows);
    });
  };

  return {
    toggleSectionInSelection,
    allCodesSelected,
    addAllCodesToSelection,
    clearAllCodesFromSelection,
    isSectionSelected,
    toggleCodeInSelection,
    areAllSymbolsSelected,
    areAnySymbolsSelected,
    isSpotLocked,
    isSymbolSelected
  };
};

export default useCommoditiesSelection;
